import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Grid from '@mui/material/Grid'
import Typography from '@mui/material/Typography'
import SessionHelper from '../../helper/session.helper'
import { signInSSO, signInSSOPKCE } from '../../services/auth/auth.service'
import Alert from '@mui/material/Alert'
import { permissionsMenu } from '../../store/actions/menuActions'
import { useNavigate } from 'react-router-dom'
import { handlerErrorV2 } from '../../utils/errors'
import ButtonGoogleSSO from '../../components/button/sso/ButtonGoogleSSO'
import ButtonMicrosoftSSO from '../../components/button/sso/ButtonMicrosoftSSO'
import ButtonOIDC from '../../components/button/sso/ButtonOIDC'
import Loading from '../../components/Loading/Loading'

const SSOButton = () => {
  const sso = useSelector((state) => state?.tenant?.sso?.filter((item) => item.isEnabled) || [])
  const licenses = useSelector((state) => state?.tenant?.license)
  const featureFlag = useSelector((state) => state?.tenant?.featureFlag || {})
  const [errors, setInputErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const redirectTo = localStorage.getItem('redirect') || '/dashboard'

  const handleSessionResult = (result) => {
    if (!result.data && result.error) {
      setInputErrors({ message: 'Invalid credentials' })
    } else {
      SessionHelper.saveSession(result.data)
      dispatch(permissionsMenu(result.data, null, licenses, featureFlag))
      navigate(redirectTo)
      localStorage.removeItem('redirect')
    }
  }

  const onLoginSSO = async (ssoId, data) => {
    try {
      setLoading(true)
      setInputErrors({ message: null })
      const result = await signInSSO(ssoId, data)
      handleSessionResult(result)
    } catch (error) {
      const message = handlerErrorV2(error)
      setInputErrors({ message })
    } finally {
      setLoading(false)
    }
  }

  const onLoginSSOPKCE = async (ssoId, data) => {
    try {
      setLoading(true)
      setInputErrors({ message: null })
      const result = await signInSSOPKCE({ idToken: data.idToken, ssoId })
      handleSessionResult(result)
    } catch (error) {
      const message = handlerErrorV2(error)
      setInputErrors({ message })
    } finally {
      setLoading(false)
    }
  }

  if (!sso.length) return <></>

  const SSOButtons = (strategy) => {
    if (strategy.service === 'google') {
      return (
        <Grid key={`service-${strategy.service}-${strategy._id}`} item xs={12}>
          <ButtonGoogleSSO
            strategy={strategy}
            onSubmit={(data) => {
              // Validar que tenemos idToken antes de llamar al backend
              if (!data.idToken) {
                console.error('Google SSO: No idToken provided')
                setInputErrors({ message: 'Authentication error. Please try again.' })
                return
              }

              onLoginSSO(strategy._id, data)
            }}
            onError={(error) => {
              setInputErrors({ message: error?.message || 'Something went wrong. Please try again.' })
            }}
            loading={loading}
          />
        </Grid>
      )
    }
    if (strategy.service === 'microsoft') {
      const isPKCE = strategy.config?.authFlow === 'authorization_code_pkce'
      return (
        <Grid key={`service-${strategy.service}-${strategy._id}`} item xs={12}>
          <ButtonMicrosoftSSO
            strategy={strategy}
            loading={loading}
            onSubmit={(data) =>
              isPKCE ? onLoginSSOPKCE(strategy._id, data) : onLoginSSO(strategy._id, data)
            }
            onError={setInputErrors}
          />
        </Grid>
      )
    }

    return (
      <Grid key={`service-${strategy.service}-${strategy._id}`} item xs={12}>
        <ButtonOIDC
          strategy={strategy}
          onSubmit={(data) => onLoginSSO(strategy._id, data)}
          onError={setInputErrors}
          config={{
            authority: strategy.config.issuer,
            clientId: strategy.config.clientId,
            redirectUri: `${window.location.origin}/login`,
            scope: 'openid email',
            responseType: 'code',
            postLogoutRedirectUri: `${window.location.origin}/login`,
            prompt: 'login'
          }}
        />
      </Grid>
    )
  }

  return (
    <React.Fragment>
      <Grid
        item
        container
        className={['m-0 ', loading ? 'opacity-50 pointer-events-none' : ''].join(' ')}
        spacing={2}
        xs={12}
      >
        {sso.map((elem) => SSOButtons(elem))}
      </Grid>
      {loading && (
        <Grid item xs={12}>
          <Loading />
        </Grid>
      )}

      {errors.message && (
        <Grid item xs={12}>
          <Alert severity='error' onClose={() => setInputErrors({ message: null })}>
            {errors.message}
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} container alignItems='center'>
        <Grid item xs={true}>
          <div className='divider-ligth-bottom color-primary-blue' />
        </Grid>
        <Grid item xs={1}>
          <Typography className='text-main text-center color-primary-blue text-bold'>or</Typography>
        </Grid>
        <Grid item xs={true}>
          <div className='divider-ligth-bottom color-primary-blue' />
        </Grid>
      </Grid>
    </React.Fragment>
  )
}

export default SSOButton
